import React, { useContext, useEffect, useState } from 'react';
import { List, ListHeader, DataColumn, LinkColumn } from 'components/lists';
import { useAggregate } from 'hooks';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Context as AlertContext } from 'contexts/alert';
import { Config } from 'configs';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from 'components/layouts/pageTitle';
import { PageContent } from 'components/layouts/pageContent';
import { ListLayout } from 'components/layouts/listLayout';
import { NoRestaurantsBlock } from 'components/blocks/noRestaurants';
import { updateRestrictedResources } from 'api/restaurant';
import { formatMillisToDisplayDateTimeString } from 'utils/dateManagement';
import { apiActionChooser } from 'utils/apiActionChooser';

export const DiscountsAndPricing = () => {
  const { state: auth } = useContext(AuthContext);
  const { alertController } = useContext(AlertContext);
  const { state: companyState, deleteById, shouldDisplayNoRestaurantBlock, getCompanyRestaurants } = useContext(CompanyContext);
  const navigate = useNavigate();

  const [isCouponDeleting, setIsCouponDeleting] = useState(false);

  const urlRootName = 'discounts';

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  /*
  const [, integrations] = useAggregate({
    collectionName: Config.DATA.COLLECTIONS.COMPANY,
    aggregateId: '0f6cc82e-096b-4882-bd1e-0ee4b46bdd57',
    secretKey: auth.token,
    filter: { company: auth.company?.company },
  }); */

  const [findCoupons, coupons, isLoadingCoupons] = useAggregate({
    collectionName: Config.DATA.COLLECTIONS.WEB_USERS,
    aggregateId: '221d0bcd-991e-42b8-a947-5ed924d48dd6',
    secretKey: auth.token,
    filter: { company: auth.company?.company },
    aggregateIdV2: 'AdminListCoupons',
    companyIdForV2: auth.company?.company,
  });

  /*
  const requestData = {
    secretKey: auth.token,
    collectionName: Config.DATA.COLLECTIONS.DISCOUNTS,
    filter: `{ "company": ObjectId("${auth.company?.company}") }`,
    cultureCode: Config.DEFAULTS.LANGUAGE,
    loadOnInit: true,
  }; */

  // const [find, list, isLoading] = useFind(requestData);

  /*
  const onPricingEditClicked = (item) => {
    navigate(`/${urlRootName}/pricing/${item._id}`);
  };

  const onPricingDiscountDeleteClicked = async (item) => {
    await deleteById(
      {
        collectionName: Config.DATA.COLLECTIONS.DISCOUNTS,
        token: auth.token,
        id: item._id,
      },
      async () => {
        await find(requestData, true);
      },
    );
  }; */

  const onPricingCouponDeleteClicked = async (item) => {
    setIsCouponDeleting(true);

    await apiActionChooser(auth.company?.company, async () => {
      const response = await updateRestrictedResources({
        token: auth.token,
        updateKey: 'coupon_manage',
        values: {
          type: 'Delete',
          data: { _id: item._id },
        },
        alertController,
      });

      if (response && response.isAcknowledged) {
        alertController.success({ text: 'Coupon deleted' });
        await findCoupons(true);
      }
    }, async () => {
      await deleteById(
        {
          id: item._id,
          resourceV2: 'AdminCoupon',
          companyIdForV2: auth.company?.company,
        },
        async () => {
          alertController.success({ text: 'Coupon deleted' });
          await findCoupons(true);
        },
      );
    });

    setIsCouponDeleting(false);
  };

  /*
  const renderIntegrationName = (discount) => {
    if (!discount || !discount.integration_id || !integrations || integrations.length === 0) {
      return '';
    }

    if (integrations[0].iiko) {
      const foundInteg = integrations[0].iiko.find((x) => x._id === discount.integration_id);

      if (foundInteg) {
        return foundInteg.name;
      }
    }

    if (integrations[0].rkeeper) {
      const foundInteg = integrations[0].rkeeper.find((x) => x._id === discount.integration_id);

      if (foundInteg) {
        return foundInteg.name;
      }
    }

    return '';
  }; */

  if (/* isLoading !== Config.DATA.LOADING_STATUS.INITIAL_LOAD && */ isLoadingCoupons !== Config.DATA.LOADING_STATUS.INITIAL_LOAD && shouldDisplayNoRestaurantBlock()) {
    return <NoRestaurantsBlock />;
  }

  return (
    <ListLayout isLoading={isLoadingCoupons === Config.DATA.LOADING_STATUS.INITIAL_LOAD || companyState.isLoadingRestaurants}>
      <PageTitle title="Discounts" isTopLevel hideMobile />
      <PageContent>
        <ListHeader title="Coupons" description="Discounts applied with a coupon code">
          <button
            type="button"
            onClick={() => navigate(`/${urlRootName}/coupons/add-new`)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            Add new
          </button>
        </ListHeader>
        <List source={coupons} onRowClick={(item) => { navigate(`/${urlRootName}/coupons/${item._id}`); }}>
          <DataColumn
            name="Coupon"
            dbColumnName="coupon"
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Description"
            dbColumnName="description"
            headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Valid from (UTC +00:00)"
            dbColumnName="valid_from"
            render={(item) => {
              if (!item.valid_from) {
                return '';
              }

              return formatMillisToDisplayDateTimeString(item.valid_from);
            }}
            headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Valid until (UTC +00:00)"
            dbColumnName="valid_until"
            render={(item) => {
              if (!item.valid_until) {
                return '';
              }

              return formatMillisToDisplayDateTimeString(item.valid_until);
            }}
            headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <LinkColumn
            title="Delete"
            onClick={onPricingCouponDeleteClicked}
            askForConfirmation
            confirmationDescription="Are you sure you want to delete this coupon?"
            className="relative py-3.5 pl-3 sm:pr-6"
            dataCellClass={`whitespace-nowrap py-4 pl-3 pr-4 text-righ text-sm font-medium sm:pr-6 ${isCouponDeleting ? 'pointer-events-none opacity-50' : ''}`}
          />
        </List>

        {/*
          <div className="mb-6 md:mb-10" />
        <ListHeader title="Discounts" description="Discounts applied automatically on matching products">
          <button
            type="button"
            onClick={() => navigate(`/${urlRootName}/pricing/add-new`)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            Add new
          </button>
        </ListHeader>
        <List source={list} onRowClick={onPricingEditClicked}>
          <DataColumn
            name="Enabled"
            dbColumnName="enabled"
            headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Name"
            dbColumnName="name"
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Type"
            dbColumnName="mode"
            headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Amount"
            render={(item) => {
              if (item.mode === 'Fixed') {
                return item.fixed;
              }

              return `${item.percent}%`;
            }}
            headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Integration"
            dbColumnName="integration_id"
            render={(item) => {
              return renderIntegrationName(item);
            }}
            headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <LinkColumn
            title="Delete"
            onClick={onPricingDiscountDeleteClicked}
            askForConfirmation
            confirmationDescription="Are you sure you want to delete this discount? You can allways disable instead in order to have it back later."
            className="relative py-3.5 pl-3 sm:pr-6"
            dataCellClass="whitespace-nowrap py-4 pl-3 pr-4 text-righ text-sm font-medium sm:pr-6"
          />

        </List>
        */}
      </PageContent>
    </ListLayout>
  );
};
